import client from './client';
import config from '../configLoader';
import { getFormattedOffset } from '../util/DateAndTimeUtil';
import { TimesheetType } from '../interfaces/TimesheetInterface';
import { Employee } from '../interfaces/EmployeeInterface';
import { Approver, ApproverGroup, Supervisor } from '../interfaces/SupervisorInterface';
import { TimeOffRequestSummary } from '../interfaces/TimeOffRequest';
import { HolidayCalendarEntry } from '../interfaces/TimeOff';
import { PayrollTimesheetStateEditable } from '../interfaces/TimesheetEditInterface';
import TimesheetEditManager from '../managers/TimesheetEditManager';
import { getSupervisors } from './supervisorList';
import { inferSubmittedStatusKey } from './timesheetStatusInference';
import type { TimesheetStatusKey } from './timesheetStatusInference';
import {
  approveTimesheet,
  approveSupervisorApproval,
  ApproveResult,
} from './timesheet';

export interface EmployeeSummary {
  employeeID: number;
  altEmployeeID: string;
  name: { first: string; middle: string; last: string };
  timesheetID: number;
  totalWorkMinutes: number;
  totalClaimedAmount: number;
  status: TimesheetType;
  supervisorName?: string;
  hasAlerts?: boolean;
  hasPendingTimeOff?: boolean;
}

export interface DriveTimeEntry {
  driveTimeEntryID: number;
  employeeID: number;
  payPeriodID: number;
  workDate: string;
  direction: 'BOD' | 'EOD';
  totalDriveMinutes: number;
  thresholdMinutes: number;
  excessMinutes: number;
}

/** Fetch employees grouped by timesheet status for a supervisor and pay period. */
export const getEmployeesForSupervisor = async (
  supervisorID: number,
  payPeriodID: number
): Promise<{
  unsubmitted: EmployeeSummary[];
  submitted: EmployeeSummary[];
  supervisorApproved: EmployeeSummary[];
  payrollApproved: EmployeeSummary[];
}> => {
  const params = { supervisorID, payPeriodID };

  const [unsubmittedRes, submittedRes, superApprovedRes, payrollApprovedRes] = await Promise.all([
    client.get('/admin/timesheets/unsubmitted', { params }).catch(() => ({ data: [] })),
    client.get('/admin/timesheets/submitted', { params }).catch(() => ({ data: [] })),
    client.get('/admin/timesheets/supervisorApproved', { params }).catch(() => ({ data: [] })),
    client.get('/admin/timesheets/payrollApproved', { params }).catch(() => ({ data: [] })),
  ]);

  const tag = (arr: any[], status: TimesheetType): EmployeeSummary[] =>
    (Array.isArray(arr) ? arr : []).map((e: any) => ({
      employeeID: e.employeeID,
      altEmployeeID: e.altEmployeeID ?? '',
      name: e.name ?? { first: '', middle: '', last: '' },
      timesheetID: e.timesheetID ?? 0,
      totalWorkMinutes: e.totalWorkMinutes ?? 0,
      totalClaimedAmount: e.totalClaimedAmount ?? 0,
      status,
      supervisorName: e.supervisorName,
    }));

  return {
    unsubmitted: tag(unsubmittedRes.data, TimesheetType.Unsubmitted),
    submitted: tag(submittedRes.data, TimesheetType.Submitted),
    supervisorApproved: tag(superApprovedRes.data, TimesheetType.SuperApproved),
    payrollApproved: tag(payrollApprovedRes.data, TimesheetType.PayApproved),
  };
};

/** Payroll Timesheet Modifications (M1-B2): whether the tenant runs the payroll
 *  submit/edit feature. When true, unsubmitted weeks load through
 *  `GET /admin/timesheetEdit/state` — a relaxed read that, unlike the legacy
 *  route, returns inactive employees and punch-less (task/sick-only) weeks
 *  instead of null, so the rows the Unsubmitted bucket now surfaces open
 *  instead of dead-ending. When false, the legacy path below runs unchanged. */
const payrollEditingEnabled = (): boolean => config.payrollTimesheetEditing?.enabled === true;

/** Map the /state editable shape into the console's Employee view-model.
 *  Near-passthrough on purpose — Shape B extends the exact projection the
 *  legacy unsubmitted read renders (`days` is consumed unchanged), plus the
 *  additive `active` flag. No Timesheet row exists yet, so timesheetID is 0. */
const mapEditableStateToEmployee = (state: PayrollTimesheetStateEditable): Employee => ({
  altEmployeeID: state.altEmployeeID,
  employeeID: state.employeeID,
  role: state.role,
  jobTitle: state.jobTitle,
  hourlyRate: state.hourlyRate,
  name: state.name,
  phoneNumber: state.phoneNumber,
  timesheetID: 0,
  days: state.days,
  active: state.active,
});

/** Infer which bucket a SUBMITTED timesheet belongs in from its approval records.
 *  The rules live in `timesheetStatusInference` (pure, unit-tested); the tenant's
 *  manager-approval gate is applied here so a tenant that never shows the stage can
 *  never be handed it. */
const inferStatusKey = (employee: Employee): TimesheetStatusKey =>
  inferSubmittedStatusKey(employee, config.allowManagerApproval === true);

/** Fetch full timesheet data for an employee — submitted or unsubmitted. */
export const getTimesheetForReview = async (
  employeeID: number,
  payPeriodID: number,
  status: TimesheetType
): Promise<Employee> => {
  if (status === TimesheetType.Unsubmitted && payrollEditingEnabled()) {
    try {
      const state = await TimesheetEditManager.loadState(employeeID, payPeriodID);
      if (state.exists) {
        // Submitted mid-flow (or a stale bucket) — load the real timesheet
        // through the normal submitted path instead of an empty unsubmitted view.
        const response = await client.get('/admin/timesheets/getEmployeeTimesheet', {
          params: { employeeID, payPeriodID },
        });
        return response.data;
      }
      return mapEditableStateToEmployee(state);
    } catch (error) {
      console.error('timesheetEdit/state failed — falling back to legacy unsubmitted read:', error);
      // fall through to the legacy route
    }
  }

  const endpoint =
    status === TimesheetType.Unsubmitted
      ? '/admin/timesheets/getUnsubmittedEmployeeTimesheet'
      : '/admin/timesheets/getEmployeeTimesheet';

  const response = await client.get(endpoint, { params: { employeeID, payPeriodID } });
  return response.data;
};

/** Re-exported so existing importers keep their path. `managerPending` (NPM's
 *  1st Approval stage) is part of the union — the board's `StatusKey` includes it. */
export type { TimesheetStatusKey } from './timesheetStatusInference';

/**
 * Fetch a timesheet regardless of which status bucket it's in.
 * Tries both endpoints in parallel and uses whichever succeeds.
 * Infers the actual status from the returned approval records.
 * Returns null if the employee has no timesheet for this pay period.
 *
 * M1-B3: when the unsubmitted week loaded through `timesheetEdit/state`, the raw
 * editable state rides along as `payrollState` — the board needs its
 * `entryCounts` (Submit disabled at zero), `timesheetQuestions` and day
 * collections (the submit wizard's receipt + exact-match ID arrays). Absent on
 * submitted weeks and on the legacy fallback path.
 */
export const getTimesheetForAnyStatus = async (
  employeeID: number,
  payPeriodID: number
): Promise<{ employee: Employee; statusKey: TimesheetStatusKey; payrollState?: PayrollTimesheetStateEditable } | null> => {
  if (payrollEditingEnabled()) {
    const [submittedProbe, stateRes] = await Promise.allSettled([
      client.get('/admin/timesheets/getEmployeeTimesheet', { params: { employeeID, payPeriodID } }),
      TimesheetEditManager.loadState(employeeID, payPeriodID),
    ]);

    if (submittedProbe.status === 'fulfilled' && submittedProbe.value?.data) {
      const employee: Employee = submittedProbe.value.data;
      return { employee, statusKey: inferStatusKey(employee) };
    }

    if (stateRes.status === 'fulfilled') {
      const state = stateRes.value;
      if (!state.exists) {
        return { employee: mapEditableStateToEmployee(state), statusKey: 'unsubmitted', payrollState: state };
      }
      // The state says a Timesheet row exists but the parallel submitted read
      // missed it (submitted mid-flow) — retry the submitted path once.
      try {
        const retry = await client.get('/admin/timesheets/getEmployeeTimesheet', {
          params: { employeeID, payPeriodID },
        });
        if (retry?.data) {
          const employee: Employee = retry.data;
          return { employee, statusKey: inferStatusKey(employee) };
        }
      } catch (error) {
        console.error('Submitted timesheet read failed after state reported exists:', error);
      }
      return null;
    }

    // The state endpoint errored — fall back to the legacy unsubmitted read.
    console.error('timesheetEdit/state failed — falling back to legacy unsubmitted read:', stateRes.reason);
    try {
      const legacy = await client.get('/admin/timesheets/getUnsubmittedEmployeeTimesheet', {
        params: { employeeID, payPeriodID },
      });
      if (legacy?.data) {
        return { employee: legacy.data, statusKey: 'unsubmitted' };
      }
    } catch {
      // fall through — no timesheet found for this pay period
    }
    return null;
  }

  const [submittedRes, unsubmittedRes] = await Promise.allSettled([
    client.get('/admin/timesheets/getEmployeeTimesheet', { params: { employeeID, payPeriodID } }),
    client.get('/admin/timesheets/getUnsubmittedEmployeeTimesheet', { params: { employeeID, payPeriodID } }),
  ]);

  if (submittedRes.status === 'fulfilled' && submittedRes.value?.data) {
    const employee: Employee = submittedRes.value.data;
    // Same derivation as the payroll-editing branch above — this is the path NPM
    // actually takes, and the one that used to answer supervisorApproved for a week
    // whose required-approver rows were merely present.
    return { employee, statusKey: inferStatusKey(employee) };
  }

  if (unsubmittedRes.status === 'fulfilled' && unsubmittedRes.value?.data) {
    return { employee: unsubmittedRes.value.data, statusKey: 'unsubmitted' };
  }

  return null;
};

/** Fetch drive time entries for an employee in a pay period via the admin endpoint. */
export const getDriveTimeEntries = async (
  employeeID: number,
  payPeriodID: number
): Promise<DriveTimeEntry[]> => {
  try {
    const response = await client.get(`/admin/employees/${employeeID}/driveTimeEntries`, {
      params: { payPeriodID },
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch {
    return [];
  }
};

/** Fetch time-off requests for an employee, filtered client-side to the given week range. */
export const getEmployeeTimeOffForWeek = async (
  employeeID: number,
  weekStart: string,
  weekEnd: string
): Promise<TimeOffRequestSummary[]> => {
  try {
    const response = await client.get(`/admin/employees/${employeeID}/timeOff/requests`);
    const all: TimeOffRequestSummary[] = Array.isArray(response.data) ? response.data : [];
    const start = new Date(weekStart).getTime();
    const end = new Date(weekEnd).getTime();
    return all.filter((r) => {
      const rStart = new Date(r.startDate).getTime();
      const rEnd = new Date(r.endDate).getTime();
      return rStart <= end && rEnd >= start;
    });
  } catch {
    return [];
  }
};

/** Fetch supervisors with their employee lists for a given timesheet status bucket.
 *  Used by TimesheetReview2Page to populate the supervisor list screen. */
export const getSupervisorsByStatus = async (
  payPeriodID: number,
  type: TimesheetType,
  adminUserEmployeeID?: number
): Promise<Supervisor[]> => {
  try {
    const response = await getSupervisors(payPeriodID, type, adminUserEmployeeID);
    return response?.data?.supervisors ?? [];
  } catch {
    return [];
  }
};

/**
 * Fetch the ManagerPending (1st Approval Pending) bucket grouped by APPROVER —
 * `GET /admin/timesheets/managerPending/approverList`. The payload is shaped
 * `{ approvers, dedupedTimesheetCount }` rather than `{ supervisors }`, and a
 * timesheet is listed under EVERY approver still owing a signature, so the row
 * count over-reports the number of timesheets.
 *
 * Approver rows are normalized onto the board's group shape (see ApproverGroup):
 * an approval group has no employee identity, so it takes the negated
 * ApprovalGroupID as its key and the group name as `name.last`.
 *
 * `dedupedTimesheetCount` is optional on purpose — an older API build omits it and
 * the board falls back to counting distinct employees client-side.
 */
export const getManagerPendingApprovers = async (
  payPeriodID: number
): Promise<{ groups: ApproverGroup[]; dedupedTimesheetCount?: number }> => {
  try {
    const response = await getSupervisors(payPeriodID, TimesheetType.ManagerPending);
    const approvers: Approver[] = response?.data?.approvers ?? [];
    const deduped = response?.data?.dedupedTimesheetCount;
    const groups: ApproverGroup[] = approvers.map((a) => {
      const isGroup = a.type === 'approvalGroup';
      return {
        kind: a.type,
        approvalGroupID: a.approvalGroupID,
        altEmployeeID: a.altEmployeeID ?? '',
        employeeID: isGroup ? -(a.approvalGroupID ?? 0) : (a.employeeID ?? 0),
        locationID: a.locationID ?? 0,
        name: isGroup
          ? { first: '', middle: '', last: a.approvalGroupName ?? 'Approval Group' }
          : { first: a.name?.first ?? '', middle: a.name?.middle ?? '', last: a.name?.last ?? '' },
        count: a.count ?? (a.employees?.length ?? 0),
        employees: a.employees ?? [],
      };
    });
    return {
      groups,
      dedupedTimesheetCount: typeof deduped === 'number' ? deduped : undefined,
    };
  } catch {
    return { groups: [] };
  }
};

/** Fetch supervisors (admin-only: for the supervisor dropdown in View A).
 *  The /admin/supervisors endpoint returns EmployeeInfo[] with flat firstName/lastName. */
export const getSupervisorList = async (): Promise<
  { employeeID: number; name: { first: string; middle: string; last: string }; altEmployeeID?: string }[]
> => {
  try {
    const response = await client.get('/admin/supervisors');
    const raw: any[] = Array.isArray(response.data) ? response.data : [];
    return raw.map((e) => ({
      employeeID: e.employeeID,
      altEmployeeID: e.altEmployeeID ?? '',
      // EmployeeInfo uses flat firstName/lastName; Employee uses name.first/name.last
      name: e.name ?? {
        first: e.firstName ?? '',
        middle: e.middleName ?? '',
        last: e.lastName ?? '',
      },
    }));
  } catch {
    return [];
  }
};

/**
 * Reassign ONE timesheet to a different supervisor, for that pay period only. The
 * server re-points Timesheet.SupervisorID (plus RequiredSuperApprovals and the summary
 * snapshot's denormalized supervisor) and writes an audit row; the employee's standing
 * hierarchy is untouched.
 *
 * A v2-shaped wrapper over the same PUT the v1 modal uses. The v1 helper in api/timesheet
 * returns the raw axios response and replaces the error with a generic Error, losing the
 * server's message; this one returns the body and rethrows the original so the board's
 * `result.success === false` check and `apiErrorMessage(err)` both work. Authorization
 * denials arrive as a real 403 with a reason (the API's catch rethrows status 403).
 */
export const reassignTimesheetSupervisor = async (
  timesheetID: number,
  supervisorID: number,
  adminEmployeeID: number
): Promise<ApproveResult> => {
  const response = await client.put('/admin/timesheet/updateSupervisor', {
    timesheetID,
    supervisorID,
    adminEmployeeID,
  });
  return response.data as ApproveResult;
};

/** Fetch holiday calendar entries applicable to an employee within a date range.
 *  The backend resolves the employee's assigned time-off / holiday policy. */
export const getEmployeeHolidays = async (
  employeeID: number,
  startDate: string,
  endDate: string
): Promise<HolidayCalendarEntry[]> => {
  try {
    const response = await client.get(`/admin/employees/${employeeID}/holidays`, {
      params: { startDate, endDate },
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch {
    return [];
  }
};

/**
 * Approve a timesheet directly to PayrollApproved in a single call.
 *
 * Primary: POST /admin/timesheet/approveToPayroll, which answers 200 with
 * `{ success: true, finalStatus: 'PayrollApproved' | 'SupervisorApproved' }`. The endpoint may
 * deliberately stop short of payroll approval — e.g. a week with an approved sick time-off
 * request on a tenant running SICK_TOR_ALWAYS_REQUIRES_RATE lands in Payroll Pending
 * (`SupervisorApproved`) so payroll can enter a sick hourly rate — so callers must read
 * `finalStatus` rather than assume the card reached Payroll Approved.
 * Fallback: calls supervisor approve then payroll approve in sequence, which always ends at
 * PayrollApproved, so the fallback reports that.
 */
export const approveToPayroll = async (
  timesheetID: number,
  adminEmployeeID: number
): Promise<{ finalStatus?: string }> => {
  const approvalDateTime = new Date().toISOString();
  const approvalDateTimeOffset = getFormattedOffset(new Date());

  try {
    const response = await client.post('/admin/timesheet/approveToPayroll', {
      timesheetID,
      adminEmployeeID,
      approvalDateTime,
      approvalDateTimeOffset,
    });
    return { finalStatus: response?.data?.finalStatus };
  } catch (err: any) {
    // If the dedicated endpoint does not exist (404 / 405), fall back to two-step
    const status = err?.response?.status;
    if (status === 404 || status === 405 || status === 501) {
      await approveSupervisorApproval(timesheetID, adminEmployeeID);
      await approveTimesheet(timesheetID, adminEmployeeID, TimesheetType.SuperApproved);
      return { finalStatus: 'PayrollApproved' };
    }
    throw err;
  }
};

/**
 * Supervisor-approve only — the first hop of the two-stage flow used by tenants without
 * useSingleHopPayrollApproval. Lands the timesheet in Supervisor Approved; payroll approval
 * is then a separate action from that bucket.
 *
 * Wraps api/timesheet's approveSupervisorApproval, which returns the raw axios response, so
 * callers can inspect the HTTP-200 { success:false, errorMessage } business-failure shape
 * that /supervisor/approve actually uses.
 */
export const approveSupervisorOnly = async (
  timesheetID: number,
  adminEmployeeID: number
): Promise<ApproveResult> => {
  const response = await approveSupervisorApproval(timesheetID, adminEmployeeID);
  return response?.data as ApproveResult;
};
